/**
 * Suivi Anti-Réclamations — 2026-09-08.
 *
 * Cette page centralise le dispositif pour éviter les réclamations de notes :
 * - Détection automatique des matières problématiques (vides, tous à 0, etc.)
 * - Bouton « Auto-remplir les cases vides avec 0 » par matière/classe
 * - Filtre semestre/classe/niveau
 *
 * Règle métier du remplissage : on ne met des 0 dans les cases vides QUE si
 * au moins un étudiant de la matière a une note >0. Les notes déjà saisies
 * ne sont JAMAIS écrasées.
 */
import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, Zap, RefreshCw, Filter, ChevronDown, ChevronUp,
  Check, X, ShieldAlert, ShieldCheck, Info, Eye, Loader2,
} from 'lucide-react';
import {
  useAuditAntiReclamationsQuery, useAutoRemplirZerosMutation,
  useLazyApercuNotesMatiereClasseQuery,
  type AuditAlerteEntry, type ApercuNotesResponse,
} from '../../services/notesDevoirsApi';
import { useGetAnneeActiveQuery, useGetAnneesAcademiquesQuery } from '../../services/anneeAcademiqueApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';

const SEVERITE_STYLE: Record<string, { bg: string; text: string; border: string; icon: React.ReactNode; label: string }> = {
  critique: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-800 dark:text-red-300',
    border: 'border-red-300 dark:border-red-800',
    icon: <ShieldAlert size={14} />,
    label: 'Critique',
  },
  alerte: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-orange-800 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-800',
    icon: <AlertTriangle size={14} />,
    label: 'Alerte',
  },
  attention: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-800 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-800',
    icon: <Info size={14} />,
    label: 'Attention',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-800 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-800',
    icon: <Info size={14} />,
    label: 'Info',
  },
};

export default function SuiviAntiReclamations() {
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data: annees = [] } = useGetAnneesAcademiquesQuery();
  const { data: allClasses = [] } = useGetClassesQuery({ actif: true });
  const [annee, setAnnee] = useState<string>('');
  const [semestre, setSemestre] = useState<'S1' | 'S2' | ''>('S2');
  const [classe, setClasse] = useState<string>('');
  const [niveau, setNiveau] = useState<string>('');
  const [pole, setPole] = useState<string>('');
  const [seuilMoitie, setSeuilMoitie] = useState<number>(0.5);
  const [severiteFiltre, setSeveriteFiltre] = useState<string>('all');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [applyResult, setApplyResult] = useState<{ key: string; msg: string; ok: boolean } | null>(null);
  // Fix 2026-09-08 : modal aperçu notes
  const [apercuOpen, setApercuOpen] = useState<{ matiereId: number; matiereNom: string; matiereCode: string; classe: string } | null>(null);
  const [fetchApercu, apercuState] = useLazyApercuNotesMatiereClasseQuery();
  const [apercuData, setApercuData] = useState<ApercuNotesResponse | null>(null);

  const classesFiltrees = useMemo(() => {
    let list = [...allClasses];
    if (niveau) list = list.filter(c => c.niveau === niveau);
    if (pole) list = list.filter(c => (c as any).pole_nom === pole);
    return list.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
  }, [allClasses, niveau, pole]);

  React.useEffect(() => {
    if (anneeActive?.libelle && !annee) setAnnee(anneeActive.libelle);
  }, [anneeActive, annee]);

  const params = {
    annee_academique: annee || undefined,
    semestre: (semestre || undefined) as 'S1' | 'S2' | undefined,
    classe: classe || undefined,
    niveau: niveau || undefined,
    pole: pole || undefined,
    seuil_moitie: seuilMoitie,
  };
  const { data, isLoading, isFetching, refetch } = useAuditAntiReclamationsQuery(params, { skip: !annee });

  const [autoRemplir, { isLoading: applyLoading }] = useAutoRemplirZerosMutation();

  const alertes = data?.alertes || [];
  const alertesFiltrees = useMemo(() => {
    if (severiteFiltre === 'all') return alertes;
    return alertes.filter(a => a.severite === severiteFiltre);
  }, [alertes, severiteFiltre]);

  // Regrouper par (matiere_id, classe) pour affichage
  const groupes = useMemo(() => {
    const map = new Map<string, { key: string; matiere: AuditAlerteEntry; alertes: AuditAlerteEntry[] }>();
    for (const a of alertesFiltrees) {
      const k = `${a.matiere_id}|${a.classe}`;
      if (!map.has(k)) map.set(k, { key: k, matiere: a, alertes: [] });
      map.get(k)!.alertes.push(a);
    }
    return Array.from(map.values());
  }, [alertesFiltrees]);

  const compteurs = useMemo(() => {
    const c: Record<string, number> = { critique: 0, alerte: 0, attention: 0, info: 0 };
    alertes.forEach(a => { c[a.severite] = (c[a.severite] || 0) + 1; });
    return c;
  }, [alertes]);

  const handleAutoRemplir = async (matiereId: number, classeCible: string, cible: 'devoirs' | 'examen' | 'both') => {
    if (!annee || !semestre) return;
    const key = `${matiereId}|${classeCible}|${cible}`;
    const confirm = window.confirm(
      `Remplir toutes les cases vides avec 0 pour la matière ${matiereId} (${classeCible}) — ${cible} ?\n\n` +
      `Rappel : seules les cases VIDES seront modifiées, les notes déjà saisies ne seront JAMAIS écrasées. ` +
      `Le remplissage est bloqué s'il n'y a aucune note >0 dans la matière (garde-fou).`
    );
    if (!confirm) return;
    try {
      const res = await autoRemplir({
        matiere_id: matiereId,
        annee_academique: annee,
        semestre: semestre as 'S1' | 'S2',
        classes: [classeCible],
        cible,
        seuil_min_notes: 1,
      }).unwrap();
      const total = res.d1_remplis + res.d2_remplis + res.examens_remplis;
      setApplyResult({
        key, ok: true,
        msg: total > 0
          ? `✓ ${res.d1_remplis} D1 + ${res.d2_remplis} D2 + ${res.examens_remplis} examens remplis (${res.nb_etudiants} étudiants)`
          : `Aucune case vide à remplir (garde-fou : dev=${res.garde_fou.gf_devoirs_ok ? 'OK' : 'insuffisant'}, ex=${res.garde_fou.gf_examens_ok ? 'OK' : 'insuffisant'})`,
      });
      refetch();
    } catch (e: any) {
      setApplyResult({ key, ok: false, msg: e?.data?.error || 'Erreur remplissage' });
    }
    setTimeout(() => setApplyResult(null), 6000);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ShieldAlert size={22} className="text-orange-500" />
            Suivi Anti-Réclamations
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Détecte les matières problématiques (notes vides, mélange de 0, session non saisie…) et permet
            d'auto-remplir les cases vides avec 0 en toute sécurité.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-gray-500" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Filtres</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Année</label>
            <select value={annee} onChange={(e) => setAnnee(e.target.value)}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700">
              {annees.map(a => <option key={a.id} value={a.libelle}>{a.libelle}{a.est_active ? ' (active)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Semestre</label>
            <select value={semestre} onChange={(e) => setSemestre(e.target.value as any)}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700">
              <option value="">Les deux</option>
              <option value="S1">S1 (impair)</option>
              <option value="S2">S2 (pair)</option>
            </select>
          </div>
          {/* Fix 2026-09-08 : filtre pôle */}
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Pôle</label>
            <select value={pole} onChange={(e) => { setPole(e.target.value); setClasse(''); }}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700">
              <option value="">Tous</option>
              {['Polytechnique', 'Commerce', 'Juridique'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Niveau</label>
            <select value={niveau} onChange={(e) => { setNiveau(e.target.value); setClasse(''); }}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700">
              <option value="">Tous</option>
              {['L1', 'L2', 'L3', 'M1', 'M2'].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Classe</label>
            {/* Fix 2026-09-08 : dropdown au lieu de saisie texte */}
            <select
              value={classe}
              onChange={(e) => setClasse(e.target.value)}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700"
            >
              <option value="">Toutes ({classesFiltrees.length})</option>
              {classesFiltrees.map(c => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.option_nom}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Seuil «moitié»</label>
            <select value={seuilMoitie} onChange={(e) => setSeuilMoitie(parseFloat(e.target.value))}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700">
              <option value={0.3}>≥ 30%</option>
              <option value={0.5}>≥ 50%</option>
              <option value={0.7}>≥ 70%</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Sévérité</label>
            <select value={severiteFiltre} onChange={(e) => setSeveriteFiltre(e.target.value)}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700">
              <option value="all">Toutes</option>
              <option value="critique">Critique</option>
              <option value="alerte">Alerte</option>
              <option value="attention">Attention</option>
            </select>
          </div>
        </div>
      </div>

      {/* KPI compteurs par sévérité */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {(['critique', 'alerte', 'attention', 'info'] as const).map(s => {
          const sty = SEVERITE_STYLE[s];
          return (
            <div key={s} className={`${sty.bg} ${sty.border} border rounded-lg p-3`}>
              <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase ${sty.text}`}>
                {sty.icon} {sty.label}
              </div>
              <div className={`text-2xl font-bold mt-1 ${sty.text}`}>{compteurs[s] || 0}</div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">alerte{(compteurs[s] || 0) > 1 ? 's' : ''}</div>
            </div>
          );
        })}
      </div>

      {/* Toast résultat */}
      {applyResult && (
        <div className={`mb-3 p-3 rounded border text-sm ${
          applyResult.ok ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-red-50 border-red-300 text-red-800'
        }`}>
          {applyResult.ok ? <Check size={14} className="inline mr-1" /> : <X size={14} className="inline mr-1" />}
          {applyResult.msg}
        </div>
      )}

      {/* Liste des alertes groupées */}
      {isLoading && (
        <div className="text-center py-10 text-gray-500">Chargement…</div>
      )}
      {!isLoading && groupes.length === 0 && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300 dark:border-emerald-800 rounded-lg p-8 text-center">
          <ShieldCheck size={40} className="mx-auto text-emerald-500 mb-2" />
          <div className="text-emerald-800 dark:text-emerald-300 font-semibold">Aucune alerte détectée</div>
          <div className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">
            Toutes les matières scannées ({data?.nb_matieres_scannees || 0}) sont conformes selon les seuils actuels.
          </div>
        </div>
      )}
      {!isLoading && groupes.length > 0 && (
        <div className="space-y-2">
          {groupes.map(g => {
            const sev = g.alertes.reduce((max, a) => {
              const order = { critique: 0, alerte: 1, attention: 2, info: 3 };
              return order[a.severite] < order[max as keyof typeof order] ? a.severite : max;
            }, 'info');
            const sty = SEVERITE_STYLE[sev];
            const isOpen = expanded[g.key] || false;
            return (
              <div key={g.key} className={`bg-white dark:bg-gray-800 rounded-lg border ${sty.border} overflow-hidden`}>
                <button
                  onClick={() => setExpanded(prev => ({ ...prev, [g.key]: !isOpen }))}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded uppercase ${sty.text} ${sty.bg} border ${sty.border}`}>
                    {sty.icon} {sty.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                      {g.matiere.matiere_nom}
                      <span className="ml-1.5 text-[11px] font-mono text-gray-500">{g.matiere.matiere_code}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Classe <span className="font-semibold text-gray-700 dark:text-gray-200">{g.matiere.classe}</span>
                      {' · '}{g.matiere.niveau}
                      {' · '}{g.matiere.semestre_relatif}
                      {' · '}{g.matiere.nb_etudiants} étudiants
                    </div>
                    {/* Fix 2026-09-08 : formateur attribué */}
                    {g.matiere.enseignant_label && (
                      <div className="text-[11px] mt-0.5 inline-flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
                        </svg>
                        <span className={g.matiere.enseignant_label === 'Non attribué' ? 'italic text-gray-500' : 'font-medium'}>
                          {g.matiere.enseignant_label}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{g.alertes.length} problème{g.alertes.length > 1 ? 's' : ''}</span>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {isOpen && (
                  <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                    {g.alertes.map((a, i) => {
                      const s = SEVERITE_STYLE[a.severite];
                      return (
                        <div key={i} className={`px-4 py-2 flex items-start gap-2 ${s.bg}`}>
                          <span className={`mt-0.5 ${s.text}`}>{s.icon}</span>
                          <div className="flex-1 text-sm">
                            <div className={s.text}>{a.message}</div>
                            <div className="text-[10px] text-gray-500 mt-0.5 font-mono">type: {a.type_alerte}</div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Barre stats consolidées (aligné Consultation notes) */}
                    {g.matiere.stats && (
                      <div className="px-4 py-2 bg-white dark:bg-gray-800/60 grid grid-cols-4 gap-2 text-center text-[11px]">
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded px-1 py-1">
                          <div className="text-[9px] uppercase text-blue-700 dark:text-blue-300 font-semibold">Devoir ✓</div>
                          <div className="text-base font-bold text-blue-700 dark:text-blue-300">{g.matiere.stats.nb_avec_devoir ?? 0}</div>
                        </div>
                        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded px-1 py-1">
                          <div className="text-[9px] uppercase text-orange-700 dark:text-orange-300 font-semibold">Devoir ✗</div>
                          <div className="text-base font-bold text-orange-700 dark:text-orange-300">{g.matiere.stats.nb_sans_devoir ?? 0}</div>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded px-1 py-1">
                          <div className="text-[9px] uppercase text-green-700 dark:text-green-300 font-semibold">Examen ✓</div>
                          <div className="text-base font-bold text-green-700 dark:text-green-300">{g.matiere.stats.nb_avec_examen ?? 0}</div>
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-1 py-1">
                          <div className="text-[9px] uppercase text-red-700 dark:text-red-300 font-semibold">Examen ✗</div>
                          <div className="text-base font-bold text-red-700 dark:text-red-300">{g.matiere.stats.nb_sans_examen ?? 0}</div>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 flex flex-wrap items-center gap-2">
                      <button
                        onClick={async () => {
                          setApercuOpen({
                            matiereId: g.matiere.matiere_id,
                            matiereNom: g.matiere.matiere_nom,
                            matiereCode: g.matiere.matiere_code,
                            classe: g.matiere.classe,
                          });
                          setApercuData(null);
                          try {
                            const r = await fetchApercu({
                              matiere_id: g.matiere.matiere_id,
                              classe: g.matiere.classe,
                              annee_academique: annee,
                              semestre: (semestre || 'S2') as 'S1' | 'S2',
                            }).unwrap();
                            setApercuData(r);
                          } catch { /* silencieux */ }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-slate-600 hover:bg-slate-700 text-white rounded"
                      >
                        <Eye size={11} /> Aperçu notes
                      </button>
                      <span className="text-xs text-gray-600 dark:text-gray-400 mx-1">
                        <Zap size={11} className="inline mr-0.5" /> Auto-remplissage 0 :
                      </span>
                      <button
                        onClick={() => handleAutoRemplir(g.matiere.matiere_id, g.matiere.classe, 'devoirs')}
                        disabled={applyLoading}
                        className="px-2 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded"
                      >
                        Devoirs (D1+D2)
                      </button>
                      <button
                        onClick={() => handleAutoRemplir(g.matiere.matiere_id, g.matiere.classe, 'examen')}
                        disabled={applyLoading}
                        className="px-2 py-1 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded"
                      >
                        Examen
                      </button>
                      <button
                        onClick={() => handleAutoRemplir(g.matiere.matiere_id, g.matiere.classe, 'both')}
                        disabled={applyLoading}
                        className="px-2 py-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded"
                      >
                        Tout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal aperçu notes */}
      {apercuOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setApercuOpen(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-gray-900 dark:text-gray-100 truncate">
                  Aperçu — {apercuOpen.matiereNom}
                  <span className="ml-2 text-xs font-mono font-normal text-gray-500">
                    {apercuOpen.matiereCode}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  Classe {apercuOpen.classe} · {annee} · {semestre || 'S1/S2'}
                  {apercuData?.matiere?.option && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-800 text-[10px]">
                      Option : {apercuData.matiere.option}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setApercuOpen(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X size={18} />
              </button>
            </div>
            {apercuState.isLoading || !apercuData ? (
              <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2">
                <Loader2 className="animate-spin" size={16} /> Chargement…
              </div>
            ) : (
              <>
                {/* Synthèse */}
                <div className="px-4 py-3 bg-slate-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 grid grid-cols-3 gap-3 text-xs">
                  {(['d1', 'd2', 'examen'] as const).map(k => {
                    const s = apercuData.synthese[k];
                    return (
                      <div key={k} className="bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2">
                        <div className="font-semibold text-gray-700 dark:text-gray-300 uppercase text-[10px] mb-1">{k}</div>
                        <div className="flex gap-2">
                          <span className="text-emerald-700 dark:text-emerald-300">✓ {s.positif}</span>
                          <span className="text-orange-700 dark:text-orange-300">0 {s.zero}</span>
                          <span className="text-red-700 dark:text-red-300">vide {s.vide}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Tableau */}
                <div className="flex-1 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800 text-xs uppercase text-gray-600 dark:text-gray-400">
                      <tr>
                        <th className="text-left px-3 py-2">Matricule</th>
                        <th className="text-left px-3 py-2">Nom</th>
                        <th className="text-center px-2 py-2">D1</th>
                        <th className="text-center px-2 py-2">D2</th>
                        <th className="text-center px-2 py-2">Moy.Dev</th>
                        <th className="text-center px-2 py-2">Examen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {apercuData.lignes.map(l => (
                        <tr key={l.etudiant_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-1.5 font-mono text-xs text-gray-500">{l.matricule}</td>
                          <td className="px-3 py-1.5">{l.nom} <span className="text-gray-500">{l.prenoms}</span></td>
                          <td className="text-center"><CellNote c={l.d1} /></td>
                          <td className="text-center"><CellNote c={l.d2} /></td>
                          <td className="text-center text-xs">
                            {l.moyenne_devoirs !== null ? l.moyenne_devoirs.toFixed(2) : '—'}
                          </td>
                          <td className="text-center"><CellNote c={l.examen} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CellNote({ c }: { c: { valeur: number | null; etat: string } }) {
  const cls =
    c.etat === 'vide' ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 border-dashed' :
    c.etat === 'absent' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300' :
    c.etat === 'zero' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-300' :
    'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-300';
  const txt =
    c.etat === 'vide' ? '—' :
    c.etat === 'absent' ? 'Abs' :
    (c.valeur ?? 0).toFixed(2);
  return (
    <span className={`inline-block min-w-[42px] px-1.5 py-0.5 text-xs font-mono font-semibold rounded border ${cls}`}>
      {txt}
    </span>
  );
}
